import React, { useState, useRef } from 'react';
import styles from './CardDetails.module.css';
import { Button } from 'react-bootstrap';
import { AttachmentIcon } from '../../../components/icons/Icons';
import { useSelector, useDispatch } from 'react-redux';
import { setFormField } from '../../../slicers/PatientSlice';
import useAxios from '../../../services/AxiosConfig';
import PainLocationView from '../../../components/PainLocationView';
import { refreshNewReportCount } from '../../../utils/sidebarUtils';
import ProfileIcon from '../../../components/ProfileIcon';
import { formatTimeAndFullMonth } from '../../../utils/dateUtils';

const ReportsDetails = ({ data }) => {
    const [noteType, setNoteType] = useState("patient");
    const [internalNewNote, setInternalNewNote] = useState("");
    const [patientNewNote, setPatientNewNote] = useState("");
    const [responses, setResponses] = useState(data.responses);
    const [unread, setUnread] = useState(data.Unread);

    const [attachments, setAttachments] = useState([]);
    const fileInputRef = useRef(null);

    const dispatch = useDispatch();
    const axiosConfig = useSelector((state) => state.axiosConfig);
    const axios = useAxios(axiosConfig.envURL);
    const patient = useSelector((state) => state.patient);
    const reports = useSelector((state) => state.patient.reports) || [];

    const userAccountId = useSelector((state) => state.userInfo.id);
    const {firstName, lastName, dateFormat} = useSelector((state) => state.userInfo);
    const reportAttachments = data.attachments || [];


    const severityMapping = (level) => {
        switch (level) {
            case 0:
                return "No Pain (0)"
            case 1:
                return "Mild Pain (1-3)"
            case 2:
                return "Moderate Pain (3-5)"
            case 3:
                return "Severe Pain (5-7)"
            case 4:
                return "Very Severe Pain (7-9)"
            case 5:
                return "Unbearable Pain (9-10)"
            default:
                return "Unknown";
        }
    };

    const getResponses = async () => {
        try {
            const reportResponse = await axios.get(`report/responses/${userAccountId}/${data.nonCriticalReportId}?showInternal=true`);
            if (reportResponse.data.data) {
                setResponses([reportResponse.data.data[0], ...responses]);
            } else {
                console.error('Error fetching patient info');
            }
        } catch (err) {
            console.log(err);
        }
    };

    const handlePatientNoteSubmission = async () => {

        const params = {
            nonCriticalReportId: data.nonCriticalReportId,
            senderId: userAccountId,
            response: patientNewNote,
            firstName: firstName,
            lastName: lastName,
        };

        if (attachments.length > 0) {
            const attachmentsData = await Promise.all(
                attachments.map(async (file) => ({
                    name: file.name,
                    type: file.type,
                    size: file.size,
                    lastModified: file.lastModified,
                    data: await readFileAsBase64(file),
                }))
            );
            params["files"] = attachmentsData;
        }

        try {
            const response = await axios.post(`report/response`, params);
            if (response.data.data) {
                setAttachments([]);
                setPatientNewNote("");
                setUnread(0);
                getResponses();
                if (unread > 0) {
                    const updatedReports = reports.map((r) =>
                        r.nonCriticalReportId === data.nonCriticalReportId ? { ...r, Unread: 0 } : r
                    );
                    dispatch(setFormField({ key: "reports", value: updatedReports }));
                }
                dispatch(refreshNewReportCount(axios, userAccountId, dispatch));
            }
        } catch (err) {
            console.error('Failed to send patient note:', err);
        }
    };

    const handleAttachmentChange = (e) => {
        const files = e.target.files;
        setAttachments([...attachments, ...files]);
    };

    const handleAttachmentIconClick = () => {
        fileInputRef.current.click();
    };

    const removeAttachment = (indexToRemove) => {
        setAttachments(attachments.filter((_, index) => index !== indexToRemove));
    };

    const readFileAsBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result.split(',')[1]); // Extract base64 part
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    };

    const handleInternalNoteSubmission = async () => {
        try {
            const response = await axios.post(`report/response`, {
                nonCriticalReportId: data.nonCriticalReportId,
                senderId: userAccountId,
                response: internalNewNote,
                firstName: firstName,
                lastName: lastName,
                isInternal: true
            });
            setInternalNewNote("");
            setResponses([response.data.data, ...responses]);
            // fetchResponses();
        } catch (err) {
            console.error('Failed to send patient note:', err);
        }
    };

    /** Sync helper: get display name and profile info for a note (patient, care team, or response fallback). */
    const getNoteAuthorInfo = (note) => {
        if (note.senderId === patient.id) {
            const parts = (patient.fullName || "").trim().split(/\s+/);
            return {
                firstName: parts[0] || "",
                lastName: parts[1] || "",
                profilePictureURL: patient.profilePictureURL || "",
                displayName: patient.fullName,
                isPatient: true,
            };
        }
        const staff = patient.careTeam?.find((user) => user.id === note.senderId);
        if (staff) {
            return {
                firstName: staff.firstName || "",
                lastName: staff.lastName || "",
                profilePictureURL: staff.profilePictureURL || "",
                displayName: `${staff.firstName} ${staff.lastName}`,
                isPatient: false,
            };
        }
        return {
            firstName: note.firstName || "",
            lastName: note.lastName || "",
            profilePictureURL: note.profilePictureURL || "",
            displayName: [note.firstName, note.lastName].filter(Boolean).join(" ") || "Unknown",
            isPatient: false,
        };
    };

    const internalNotes = responses.filter(note => note.isInternal);
    const patientNotes = responses.filter(note => !note.isInternal);
    const mappedNotes = noteType === "internal" ? internalNotes : patientNotes;

    return (
        <div className={styles.DetailsContainer}>
            <div className={styles.Detail}>
                <span className={styles.DetailLabelReports}>Description:</span>
                <div style={{ width: "506px", overflow: "wrap" }}>
                    {data.description}
                </div>
            </div>
            {data.painLocations &&
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", padding: "10px 0px" }}>
                    <span className={styles.DetailLabelReports}>Pain Location:</span>
                    <PainLocationView painLocations={data.painLocations} />
                </div>

            }

            {data.severity &&
                <div className={styles.Detail}>
                    <span className={styles.DetailLabelReports}>Severity Level:</span>
                    {severityMapping(data.severity)}
                </div>
            }
            {reportAttachments.length > 0 &&
                <div className={styles.Detail} style={{ zIndex: "2" }}>
                    <span className={styles.DetailLabelReports}>Images:</span>
                    <div>
                        {reportAttachments.map((file, index) =>
                            <div key={index} className={`${styles.AttachmentContainer} ${styles.Attachment}`}>
                                <a href={file.signedUrl} target="_blank" rel="noopener noreferrer" >{file.fileName}</a>
                            </div>
                        )}
                    </div>
                </div>
            }
            <div className={styles.ReportsTabs}>
                <div className={noteType === "internal" ? styles.NoteTabActive : styles.NoteTab} onClick={() => setNoteType("internal")}>
                    <span>Internal Notes</span>
                </div>
                <div className={noteType === "patient" ? styles.NoteTabActive : styles.NoteTab} onClick={() => setNoteType("patient")}>
                    <span>Reply to Patient</span>
                </div>
            </div>
            {noteType === "internal" && data.status !== "Resolved" && (
                <div style={{ width: "100%" }}>
                    <div className={styles.NewNoteSection}>
                        <textarea
                            className={styles.NewNote}
                            placeholder='Add Notes'
                            value={internalNewNote}
                            onChange={(e) => setInternalNewNote(e.target.value)}
                        >
                        </textarea>
                        <Button
                            variant="secondary"
                            className={styles.NewNoteButton}
                            onClick={handleInternalNoteSubmission}
                            disabled={internalNewNote === ""}
                        >
                            Submit
                        </Button>
                    </div>
                </div>
            )}
            {noteType === "patient" && data.status !== "Resolved" && (
                <div style={{ width: "100%", display: "flex", flexDirection: "column" }}>
                    <div className={styles.NewNoteSection}>
                        <textarea
                            className={styles.NewNote}
                            placeholder='Add Notes'
                            value={patientNewNote}
                            onChange={(e) => setPatientNewNote(e.target.value)}
                        >
                        </textarea>
                        <div className={styles.AttachedFiles}>
                            {attachments.map((file, index) => (
                                <div key={index} className={styles.AttachedFile}>
                                    <span>{file.name}</span>
                                    <Button variant="link" onClick={() => removeAttachment(index)}>Remove</Button>
                                </div>
                            ))}
                        </div>
                        <div style={{ display: "flex" }}>
                            <Button
                                variant="secondary"
                                className={styles.NewNoteButton}
                                onClick={handlePatientNoteSubmission}
                                disabled={patientNewNote === ""}
                            >
                                Submit
                            </Button>
                            <Button variant="white" onClick={handleAttachmentIconClick}>
                                <AttachmentIcon />
                            </Button>
                            <input
                                type="file"
                                multiple
                                style={{ display: 'none' }}
                                onChange={handleAttachmentChange}
                                ref={fileInputRef}
                            />
                        </div>
                    </div>
                </div>
            )}
            <div className="body-2" style={{paddingTop:"20px"}}>Notes:</div>
            {mappedNotes.length !== 0 && (
                mappedNotes.map((note, index) => {
                    const author = getNoteAuthorInfo(note);
                    return (
                    <div key={index} className={styles.ReportNotesContainer} style={{background: index < unread ? "rgba(203, 243, 210, 0.40)" : "white", borderBottom: index < mappedNotes.length - 1 ? "1px solid #e0e0e0" : "none"}}>
                        <div style={{display:"flex", width:"100%", justifyContent:"space-between", alignItems:"center",padding:"0px 10px"}}>
                            <div style={{display:"flex", columnGap:"10px", alignItems:"center"}}>
                            <ProfileIcon
                                profileUrl={author.profilePictureURL}
                                firstName={author.firstName}
                                lastName={author.lastName}
                                size={28}
                            />
                            {author.displayName}
                            </div>
                            <span style={{fontSize:"12px", fontWeight:"500"}}>{`${formatTimeAndFullMonth(note.responseDate, dateFormat)}`}</span>
                        </div>
                        <div style={{padding:"0 10px"}}>
                            {note.response}

                            {note.attachments && note.attachments.length > 0 && note.attachments.map((attachment, index) => (
                                <div key={index} className={`${styles.AttachmentContainer} ${styles.Attachment}`}>
                                    <a href={attachment.presignedURL} target="_blank" rel="noopener noreferrer" >{attachment.fileName}</a>
                                </div>
                            ))}
                        </div>
                    </div>
                    );
                })
            )}
        </div>
    )
}
export default ReportsDetails;